import clsx from "clsx";
import { useEffect, useRef } from "@wordpress/element";
import {
  InnerBlocks,
  useBlockProps,
  BlockControls,
  JustifyContentControl,
} from "@wordpress/block-editor";
import { useState } from "@wordpress/element";
import { useSelect, useDispatch } from "@wordpress/data";
import QuickViewProductInspectorControls from "./InspectorControls";

export default function Edit({ attributes, setAttributes, clientId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasInitialized = useRef(false);

  const {
    popupBackdropColor = "#00000033",
    popupBackgroundColor = "",
    popupBorder = {},
    popupShadow = "",
    popupPadding = {},
  } = attributes.style ?? {};

  const blockAlignment = attributes?.blockAlignment ?? "";

  const blockProps = useBlockProps({
    className: clsx(
      "brandy-quick-view-product-block",
      blockAlignment ? `has-block-alignment-${blockAlignment}` : ""
    ),
  });

  // Get inner blocks and dispatch functions
  const { innerBlocks } = useSelect(
    (select) => ({
      innerBlocks: select("core/block-editor").getBlocks(clientId),
    }),
    [clientId]
  );

  const { replaceInnerBlocks } = useDispatch("core/block-editor");

  // Create dynamic template with current productId
  const getTemplate = () => [
    [
      "brandy/button",
      {
        text: "Quick View",
        className: "quick-view-trigger-button",
        lock: {
          remove: true,
          move: true,
        },
      },
    ],
  ];

  // Function to ensure button block always exists
  const ensureButtonBlockExists = () => {
    if (!innerBlocks || innerBlocks.length === 0) {
      // No blocks exist, create the button block
      const template = getTemplate();
      const buttonBlock = wp.blocks.createBlock(template[0][0], template[0][1]);
      replaceInnerBlocks(clientId, [buttonBlock], false);
      return;
    }

    // Check if any button block exists
    const hasButtonBlock = innerBlocks.some(
      (block) => block.name === "brandy/button"
    );

    if (!hasButtonBlock) {
      // No button block found, add one at the beginning
      const template = getTemplate();
      const buttonBlock = wp.blocks.createBlock(template[0][0], template[0][1]);
      replaceInnerBlocks(clientId, [buttonBlock, ...innerBlocks], false);
    }
  };

  // Effect to ensure button block exists
  useEffect(() => {
    if (hasInitialized.current) {
      ensureButtonBlockExists();
    }
  }, [innerBlocks]);

  // Initialize the block with button if needed
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      ensureButtonBlockExists();
    }
  }, []);

  useEffect(() => {
    // Clean up any existing modal first
    const existingBackdrop = document.querySelector(
      `.brandy-quick-view-product-backdrop[data-quick-view-product-block="${clientId}"]`
    );
    if (existingBackdrop) {
      existingBackdrop.remove();
    }

    let escapeKeyHandler = null;

    if (isModalOpen) {
      // Add new element to the body with the class "quick-view-product-backdrop"
      const backdrop = document.createElement("div");
      backdrop.className = "brandy-quick-view-product-backdrop";
      backdrop.style.backgroundColor =
        popupBackdropColor || "#00000033";
      backdrop.dataset.quickViewProductBlock = clientId;

      // Add new element to backdrop with the class "quick-view-product-block"
      const quickViewProductBlock = document.createElement("div");
      quickViewProductBlock.className = "brandy-quick-view-product-wrapper";
      // Add new element to quickViewProductBlock with the class "quick-view-product-content"
      const quickViewProductContent = document.createElement("div");
      quickViewProductContent.className = "brandy-quick-view-product-content";
      quickViewProductContent.style.backgroundColor =
        popupBackgroundColor || "var(--wp--custom--background)";
      quickViewProductContent.style.padding = popupPadding.top || "20px";
      quickViewProductContent.style.paddingRight = popupPadding.right || "20px";
      quickViewProductContent.style.paddingBottom =
        popupPadding.bottom || "20px";
      quickViewProductContent.style.paddingLeft = popupPadding.left || "20px";
      quickViewProductContent.style.boxShadow = popupShadow || "";
      quickViewProductContent.style.borderWidth = popupBorder.width || "0px";
      quickViewProductContent.style.borderStyle = popupBorder.style || "solid";
      quickViewProductContent.style.borderColor =
        popupBorder.color || "#000000";
      quickViewProductContent.style.borderRadius = popupBorder.radius || "10px";
      quickViewProductContent.dataset.quickViewProductBlock = clientId;

      // Add new element to quickViewProductContent with the class "quick-view-product-inner-content"
      const quickViewProductInnerContent = document.createElement("div");
      quickViewProductInnerContent.className =
        "brandy-quick-view-product-inner-content";

      quickViewProductInnerContent.dataset.quickViewProductBlock = clientId;

      // Add new element to quickViewProductInnerContent with the class "quick-view-modal-content"
      const quickViewProductModalContent = document.createElement("div");
      quickViewProductModalContent.className =
        "brandy-quick-view-modal-content";
      quickViewProductModalContent.dataset.quickViewProductBlock = clientId;
      quickViewProductInnerContent.appendChild(quickViewProductModalContent);
      quickViewProductContent.appendChild(quickViewProductInnerContent);
      quickViewProductBlock.appendChild(quickViewProductContent);
      backdrop.appendChild(quickViewProductBlock);

      backdrop.addEventListener("click", (e) => {
        if (e.target.classList.contains("brandy-quick-view-product-backdrop")) {
          setIsModalOpen(false);
        }
      });
      document.body.appendChild(backdrop);

      escapeKeyHandler = (e) => {
        if (e.key === "Escape") {
          const backdrop = document.querySelector(
            ".brandy-quick-view-product-backdrop"
          );
          if (backdrop) {
            setIsModalOpen(false);
          }
        }
      };
      document.addEventListener("keydown", escapeKeyHandler);

      const modalContent = document.querySelector(
        ".brandy-quick-view-modal-content"
      );
      modalContent.innerHTML = `
        <div class="brandy-quick-view-skeleton">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-price"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-button"></div>
          </div>
        </div>
      `;
    }

    // Cleanup function
    return () => {
      if (escapeKeyHandler) {
        document.removeEventListener("keydown", escapeKeyHandler);
      }
    };
  }, [
    isModalOpen,
    clientId,
    popupBackdropColor,
    popupBackgroundColor,
    popupBorder,
    popupShadow,
    popupPadding,
  ]);

  return (
    <div {...blockProps}>
      <InnerBlocks
        template={getTemplate()}
        allowedBlocks={["brandy/button"]}
        templateLock="all"
        templateInsertUpdatesSelection={false}
        renderAppender={false}
      />
      <BlockControls group="block">
        <JustifyContentControl
          value={blockAlignment}
          onChange={(nextAlign) => {
            setAttributes({ blockAlignment: nextAlign });
          }}
          allowedControls={["left", "center", "right"]}
        />
      </BlockControls>

      <QuickViewProductInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        clientId={clientId}
      />
    </div>
  );
}
